import * as React from 'react';
import { useCallback } from 'react';

import { createLocation } from 'history';
import type { History, Location as HistoryLocation } from 'history';
import type { Location as RouterLocation } from 'react-resource-router';
import { Link, RouterSubscriber } from 'react-resource-router';

export const normalizeToLocation = (to: string, currentLocation: RouterLocation): HistoryLocation =>
	createLocation(to, null, undefined, currentLocation as HistoryLocation);

const isModifiedEvent = (event: React.MouseEvent | React.KeyboardEvent): boolean =>
	event.metaKey || event.altKey || event.ctrlKey || event.shiftKey;

export type BaseUrlRespectedLinkProps = Omit<React.ComponentPropsWithRef<typeof Link>, 'to'> & {
	to: string;
};

type PropsWithRouter = BaseUrlRespectedLinkProps & {
	history: History;
	location: RouterLocation;
};

/**
 * React Resource Router does not apply the base URL when `to` is a string. because we do not pass basePath to the router, we instead pass it to the history.
 * Keep the displayed URL base-aware while navigating with the relative router location.
 */
// The server case is intentionally not handled until platform can safely access AJS metadata.
const getHref = (history: History, location: HistoryLocation): string =>
	history.createHref(location);

const BaseUrlRespectedLink = ({
	to,
	onClick,
	history,
	location,
	target,
	replace,
	children,
	...rest
}: PropsWithRouter) => {
	const toAsLocation = normalizeToLocation(to, location);
	const href = toAsLocation ? getHref(history, toAsLocation) : '';

	const wrappedOnClick = useCallback(
		(event: React.MouseEvent | React.KeyboardEvent) => {
			try {
				onClick?.(event);
			} catch (error) {
				event.preventDefault();
				throw error;
			}

			const isPrimaryClick = 'button' in event && event.button === 0;
			const isEnterKey = 'key' in event && event.key === 'Enter';
			if (
				!event.defaultPrevented &&
				(isPrimaryClick || isEnterKey) &&
				(!target || target === '_self') &&
				!isModifiedEvent(event)
			) {
				event.preventDefault();
				const method = replace ? history.replace : history.push;
				method(normalizeToLocation(to, location));
			}
		},
		[history, location, onClick, replace, target, to],
	);

	return (
		<Link href={href} {...rest} target={target} onClick={wrappedOnClick}>
			{children}
		</Link>
	);
};

const BaseUrlRespectedLinkWithRouter = (props: BaseUrlRespectedLinkProps): React.ReactElement => (
	<RouterSubscriber>
		{(router) => {
			const { location } = router;
			const { history } = router as unknown as { history: History };

			return <BaseUrlRespectedLink location={location} history={history} {...props} />;
		}}
	</RouterSubscriber>
);

export default BaseUrlRespectedLinkWithRouter;
